import { QuickStatsCard } from "@features/cx/shared/components/QuickStatsCard";
import { AlertTriangle, Clock, TrendingUp } from "lucide-react";
import React, { useMemo } from "react";

interface ServiceOrderData {
  expectedCompletion: string;
  priority?: string;
  status?: string;
  slaStatus?: string;
  remainingTime?: string;
}

interface KPIData {
  slaStatus: string;
  slaDueDate: string;
  timeRemaining: string;
}

interface KPIMetric {
  title: string;
  count: string;
  trendSummary: string;
  icon: React.ReactNode;
  tooltip?: string;
}

// KPI calculation logic
export const calculateKPIData = (
  serviceOrderData?: ServiceOrderData
): KPIData => {
  if (!serviceOrderData) {
    return {
      slaStatus: "N/A",
      slaDueDate: "N/A",
      timeRemaining: "N/A",
    };
  }

  return {
    slaStatus: serviceOrderData.slaStatus,
    slaDueDate: serviceOrderData.expectedCompletion,
    timeRemaining: serviceOrderData.remainingTime,
  };
};

// KPI configuration
export const createKPIMetrics = (kpiData: KPIData): KPIMetric[] => [
  {
    title: "SLA Status",
    count: kpiData.slaStatus,
    trendSummary: "Within acceptable limits",
    icon: <TrendingUp className="h-8 w-8 text-green-500" />,
    tooltip: "Current status of Service Level Agreement compliance",
  },
  {
    title: "SLA Due Date",
    count: kpiData.slaDueDate,
    trendSummary: "Meeting timeline",
    icon: <Clock className="h-8 w-8 text-blue-500" />,
    tooltip: "Expected completion date for the service order",
  },
  {
    title: "Time Remaining",
    count: kpiData.timeRemaining,
    trendSummary: "On schedule",
    icon: <AlertTriangle className="h-8 w-8 text-orange-500" />,
    tooltip: "Days remaining until the due date",
  },
];

interface AssetServiceOrderKPIProps {
  serviceOrderData?: ServiceOrderData;
  className?: string;
}

// Reusable KPI Dashboard Component
export const AssetServiceOrderKPI: React.FC<AssetServiceOrderKPIProps> = ({
  serviceOrderData,
  className = "",
}) => {
  const kpiData = useMemo(
    () => calculateKPIData(serviceOrderData),
    [serviceOrderData]
  );

  const kpiMetrics = useMemo(() => createKPIMetrics(kpiData), [kpiData]);

  return (
    <div className={`grid grid-cols-1 md:grid-cols-3 gap-6 ${className}`}>
      {kpiMetrics.map((metric, index) => (
        <QuickStatsCard
          key={index}
          title={metric.title}
          count={metric.count}
          trendSummary={metric.trendSummary}
          icon={metric.icon}
          tooltip={metric.tooltip}
        />
      ))}
    </div>
  );
};

export default AssetServiceOrderKPI;
